/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useCallback } from 'preact/hooks';
import { SingleSelectionDetail } from '../../UNSAFE_Collection';

export function useSingleSelection<K>(
  keyExtractor: (element: HTMLElement) => K | null,
  selected?: K,
  onChange?: (detail: SingleSelectionDetail<K>) => void
) {
  const onClick = useCallback(
    (event: MouseEvent) => {
      handleSelect(event, keyExtractor, selected, onChange);
    },
    [keyExtractor, selected, onChange]
  );

  const onKeyUp = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        handleSelect(event, keyExtractor, selected, onChange);
      }
    },
    [keyExtractor, selected, onChange]
  );

  const selectionProps = onChange ? { onClick, onKeyDown, onKeyUp } : {};
  return { selectionProps };
}

// prevent default (propagation) for keyDown to prevent arrow keys from scrolling the container
const onKeyDown = (event: KeyboardEvent) => {
  if (event.key === 'Enter') {
    event.preventDefault();
  }
};

const handleSelect = <K>(
  event: Event,
  keyExtractor: (element: HTMLElement) => K | null,
  selected?: K,
  onChange?: (detail: SingleSelectionDetail<K>) => void
) => {
  const itemKey = keyExtractor(event.target as HTMLElement);
  if (onChange && itemKey && selected !== itemKey) {
    onChange({ value: itemKey });
    if (event.type === 'click') {
      event.stopPropagation();
    }
  }
};
